import { inject, Injectable } from '@angular/core';
import {
  AuthToken,
  DeepLinkService,
  KeycloakService,
  SessionStorageService
} from '@ui/legacy-lib';
import {
  ActivatedRouteSnapshot,
  RedirectCommand,
  Router
} from '@angular/router';
import {
  catchError,
  filter,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  take
} from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Location } from '@angular/common';
import { jwtDecode } from 'jwt-decode';
import { PropertyType } from '@ui/shared/model';
import { SearchProfileFacade } from '@ui/tenant-pool/shared/search-profile/data-access';
import { TooltipStore } from '@ui/tenant-pool/shared/tooltip/data-access';
import { storageKeys } from '@ui/tenant-pool/shared/config/util';
import { UserStore } from '@ui/tenant-pool/shared/user/data-access';
import { MainPageNavigation } from '../../config/navigation.config';
import { NAVIGATION_LINK } from '../../config/navigation-links.config';

import { OpenCompleteGarageModal } from '../../+state/property-matches/property-matches.actions';
import { UserSessionModalsService } from './user-session-modals.service';
import { PropertyMatchesFacade } from './property-matches.facade';

@Injectable({
  providedIn: 'root'
})
export class AuthGuardHelperService {
  private deepLinkService = inject(DeepLinkService);
  private keycloakService = inject(KeycloakService);
  private sessionStorage = inject(SessionStorageService);
  private store = inject(Store);
  private readonly userStore = inject(UserStore);
  /**
   * Injected for its side effect: the service is `providedIn: 'root'` and so
   * constructed lazily, and its effect is what opens the activeness and SMS
   * modals once a load settles. This is the first thing to run a load, so it
   * is the right place to bring it to life.
   */
  private readonly userSessionModals = inject(UserSessionModalsService);
  private router = inject(Router);
  private location = inject(Location);
  private searchProfileFacade = inject(SearchProfileFacade);
  private propertyMatchesFacade = inject(PropertyMatchesFacade);
  private tooltipStore = inject(TooltipStore);

  // After the first-time login check resolves (redirect to profile-edit or garage modal),
  // the AuthGuard is called again for the target route. This flag skips the full check on that re-entry.
  private skipNextGuardCheck = false;

  private getAuthToken(route: ActivatedRouteSnapshot): AuthToken {
    return route?.fragment
      ? this.deepLinkService.getSSOParams(route)
      : ({ access_token: this.keycloakService.token } as AuthToken);
  }

  public isRegistered(
    activatedRoute: ActivatedRouteSnapshot,
    redirectToUrl?: string
  ): Observable<boolean | RedirectCommand> {
    this.userStore.fetchIsRegistered();

    return this.userStore.isRegisteredActionState$.pipe(
      filter(actionState => actionState.done || !!actionState.error),
      take(1),
      switchMap(actionState =>
        of(this.userStore.isRegistered()).pipe(
          switchMap(isRegistered => {
            // Not sure when isRegistered can be null. Just took that from the old implementation
            if (actionState.error || !isRegistered) {
              return of(this.logoutUserCommand(redirectToUrl));
            }

            if (!isRegistered.vmp && isRegistered.resident) {
              return of(
                new RedirectCommand(
                  this.router.createUrlTree(
                    [NAVIGATION_LINK.TERMS_AND_CONDITIONS],
                    {
                      queryParams: activatedRoute.queryParams
                    }
                  ),
                  {
                    skipLocationChange: true
                  }
                )
              );
            }

            // When the user is not registered for either platform
            // This can happen when you use the social login.
            // If you used the social login once, then a keycloak user is created, and you can "login".
            // Usually if you get an access token, then you are registered for either platform.
            // But the social login is a special case, in which we need to redirect the user to the registration page.
            if (!isRegistered.vmp && !isRegistered.resident) {
              let queryParams = undefined;
              // Yes, 'true' is correct, because all query params are strings
              if (activatedRoute.queryParams.socialLogin === 'true') {
                const accessToken = this.keycloakService.token;

                if (accessToken) {
                  const { email, given_name, family_name } = jwtDecode<{
                    email;
                    given_name;
                    family_name;
                  }>(accessToken);
                  // Adding this data to the query params, so that the registration page can prefill the form
                  queryParams = {
                    registerAfterSocialLogin: true,
                    socialLoginEmail: email,
                    firstName: given_name,
                    lastName: family_name,
                    type: activatedRoute.queryParams.socialLoginType,
                    pathAfterAuth: activatedRoute.queryParams.pathAfterAuth
                  };
                }
              }

              return of(
                new RedirectCommand(
                  this.router.createUrlTree(
                    [
                      MainPageNavigation.REGISTRATION,
                      MainPageNavigation.REGISTRATION_PROPERTYLOCATION
                    ],
                    {
                      queryParams
                    }
                  )
                )
              );
            }

            return this.fetchUserData(redirectToUrl);
          })
        )
      ),
      switchMap(data => this.runFirstTimeLoginCheck(data)),
      catchError(() => {
        return of(this.logoutUserCommand(redirectToUrl));
      })
    );
  }

  /**
   * If a specific global tooltip is set, then the user should get redirected to the profile editing page.
   * The tooltip will only be set with the user registration
   * @param data
   * @private
   */
  private runFirstTimeLoginCheck(data: boolean | RedirectCommand) {
    if (typeof data !== 'boolean') {
      return of(data);
    }

    if (data === false) {
      return of(
        new RedirectCommand(
          this.router.parseUrl(MainPageNavigation.EMAIL_VERIFICATION_PENDING),
          {
            replaceUrl: true,
            skipLocationChange: true
          }
        )
      );
    }

    // We don't have any flag that tells if the user logged in for the first time after the registration
    // For that we abuse the tooltips now. They can be shown only for newly registered users
    return this.tooltipStore.globalTooltips().pipe(
      switchMap(tooltips => {
        // If this tooltip is set, this means that the user is logging in for the first time after registration
        const redirectTooltip = tooltips.find(
          item =>
            item.anchorPoint?.anchorPoint === 'global.profile-edit-redirect'
        );
        if (!redirectTooltip) {
          return of(data);
        }

        // Immomio Now: a tenant still awaiting a subscription lands on the lock screen instead
        if (this.userStore.awaitingSubscription()) {
          this.tooltipStore.markTooltipsSeen([redirectTooltip.id]);
          return of(data);
        }

        // Check if the user registered with a garage/parking search profile or applied for a garage property.
        // finalize ensures markTooltipsSeen and skipNextGuardCheck run in all cases (success + error).
        return forkJoin([
          this.searchProfileFacade.loadSearchProfiles().pipe(take(1)),
          this.propertyMatchesFacade
            .loadPropertyMatchesBeans({ page: 0, size: 100 })
            .pipe(take(1))
        ]).pipe(
          map(([{ searchProfiles }, applications]) => {
            const hasGarageProfile = searchProfiles?.some(
              sp => sp.propertyType === PropertyType.GARAGE
            );
            const hasGarageApplication = applications?.nodes?.some(
              app => app.property?.type === PropertyType.GARAGE
            );

            if (hasGarageProfile || hasGarageApplication) {
              // Only skip dispatch when ApplicationCreationGuard will handle it:
              // that guard opens the garage modal when propertyIdToApply exists in sessionStorage.
              const applicationCreationGuardWillHandleIt =
                hasGarageApplication &&
                !!this.sessionStorage.getItem<number | string>(
                  storageKeys.propertyIdToApply
                );

              if (!applicationCreationGuardWillHandleIt) {
                this.store.dispatch(OpenCompleteGarageModal());
              }
              return true;
            }

            return this.profileEditRedirect();
          }),
          catchError(() => of(this.profileEditRedirect())),
          finalize(() => {
            this.tooltipStore.markTooltipsSeen([redirectTooltip.id]);
            this.skipNextGuardCheck = true;
          })
        );
      })
    );
  }

  private profileEditRedirect(): RedirectCommand {
    return new RedirectCommand(
      this.router.createUrlTree([
        `/${MainPageNavigation.PROFILE}/edit/step/dkZero`
      ])
    );
  }

  private logoutUserCommand(redirectToUrl?: string) {
    return new RedirectCommand(
      this.router.createUrlTree([MainPageNavigation.LOGIN], {
        queryParams: {
          pathAfterAuth: redirectToUrl
        }
      })
    );
  }

  private assignParkedRegistrationToken(): Observable<boolean> {
    const registrationTokenId = this.sessionStorage.getItem<string>(
      storageKeys.registrationTokenId
    );

    if (!registrationTokenId) {
      return of(false);
    }
    this.sessionStorage.removeItem(storageKeys.registrationTokenId);

    return this.userStore.assignRegistrationToken(registrationTokenId).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  private fetchUserData(redirectToUrl?: string) {
    return this.assignParkedRegistrationToken().pipe(
      switchMap(tokenWasAssigned => {
        if (tokenWasAssigned) {
          this.userStore.loadUser();
        }

        return this.userStore.userDataActionState$.pipe(
          tap(state => {
            if (!state.pending && !state.done && !state.error) {
              this.userStore.loadUser();
            }
          }),
          filter(state => state.done || !!state.error),
          take(1)
        );
      }),
      switchMap(state => {
        if (state.error) {
          return of(this.logoutUserCommand(redirectToUrl));
        }
        return this.isUserVerified(redirectToUrl);
      })
    );
  }

  private isUserVerified(redirectToUrl?: string) {
    return of(this.userStore.userData()).pipe(
      map(userData => {
        if (!userData) {
          return this.logoutUserCommand(redirectToUrl);
        }
        if (redirectToUrl.includes('#')) {
          // There's some metadata included in the url by keycloak
          // This HAS to be removed from the URL. If not it will throw an error when you reload the page
          const urlWithoutHash = redirectToUrl.split('#')[0];
          this.location.replaceState(urlWithoutHash);
        }
        if (!userData.emailVerified) {
          return false;
        }

        return true;
      })
    );
  }

  public ensureUserIsAuthenticatedAndVerified(
    route: ActivatedRouteSnapshot,
    redirectToUrl?: string
  ): Observable<boolean | RedirectCommand> {
    if (this.skipNextGuardCheck) {
      this.skipNextGuardCheck = false;
      return of(true);
    }
    const authToken = this.getAuthToken(route);

    if (authToken?.access_token) {
      return this.isRegistered(route, redirectToUrl);
    } else {
      return of(this.logoutUserCommand(redirectToUrl));
    }
  }
}
